<script setup lang="ts">
/**
 * App.vue
 * ─────────────────────────────────────────────────────────────────────────────
 * Orquesta el layout, aplica clases de estado global (sidebar, header, etc.),
 * y gestiona:
 *   - barra de progreso en cambios de ruta
 *   - RetryModal para pérdidas de conexión
 *   - clase <body>.login-page cuando la vista actual es login (o kiosk-login)
 *   - renderizado del header/sidebar/top-menu/teleports
 */

import {
  ref,
  computed,
  watch,
  watchEffect,
  onMounted,
  onBeforeUnmount,
} from "vue";
import { RouterView, useRoute } from "vue-router";

import { useAppOptionStore } from "@/stores/app-option";
import { useProgress } from "@marcoschulte/vue3-progress";
import type { ProgressFinisher } from "@marcoschulte/vue3-progress";

import AppSidebar from "@/components/app/Sidebar.vue";
import AppHeader from "@/components/app/Header.vue";
import AppTopMenu from "@/components/app/TopMenu.vue";
import AppFooter from "@/components/app/Footer.vue";
import AppThemePanel from "@/components/app/ThemePanel.vue";

import router from "./router";

import RetryModal from "@/components/_common/RetryModal.vue";
import OrientationWarningMobile from "@/components/_common/OrientationWarningMobile.vue";
import { useNetworkWatcher } from "@/composables/useNetworkWatcher";
import { retryManager } from "@/composables/useRetryManager";
import { useIonicOverlayGuard } from "@/composables/useIonicOverlayGuard";
import { useIOSSwipeBackGuard } from "@/composables/useIOSSwipeBackGuard";

// 🆕 Banner Ionic de PWA para control de versión y actualización forzada
import PWAUpdateBannerIonic from "@/components/_common/PWAUpdateBanner.vue";

// ─────────────────────────────────────────────────────────────────────────────
// Inicializaciones de composables / stores
// ─────────────────────────────────────────────────────────────────────────────
useNetworkWatcher();

// previene gestos de swipe-back de Ionic si hay un overlay abierto
const { hasOverlayOpen } = useIonicOverlayGuard();
useIOSSwipeBackGuard(hasOverlayOpen, { edgePx: 24 });

const { showRetryModal, retry } = retryManager;

const appOption = useAppOptionStore();
const route = useRoute();
const orientationActive = ref(false); // si el aviso de orientación está activo
// Governed SPI-only shell exception; other applications keep the legacy policy.
const hasSpiOrientationException = computed(
  () => route.name === "internal-orders",
);
watch(
  hasSpiOrientationException,
  (enabled) => {
    orientationActive.value = false;
    if (enabled) {
      document.body.classList.remove("orientation-lock--active");
      document.body.style.top = "";
    }
  },
  { flush: "sync" },
);

// ─────────────────────────────────────────────────────────────────────────────
// Clase <body>.login-page cuando la ruta es de login o kiosk-login
// ─────────────────────────────────────────────────────────────────────────────
const isLoginRoute = computed(
  () => route.name === "login" || route.name === "kiosk-login"
);

// Aplica la clase
function setLoginBodyClass(flag: boolean) {
  document.body.classList.toggle("login-page", flag);
}

// Aplica/actualiza automáticamente cada vez que cambie la ruta
watchEffect(() => {
  setLoginBodyClass(isLoginRoute.value);
});

// ✅ popstate guard (solo si hay overlay)
const onPopState = () => {
  if (!hasOverlayOpen.value) return;

  // “deshace” el back inmediatamente (sin disparar navegación Vue extra)
  history.forward();
};

onMounted(() => {
  window.addEventListener("popstate", onPopState);
});

onBeforeUnmount(() => {
  document.body.classList.remove("login-page");
  window.removeEventListener("popstate", onPopState);
});

// ─────────────────────────────────────────────────────────────────────────────
// Barra de progreso en navegación
//    - Usamos un único "finisher" por navegación con try/finally
// ─────────────────────────────────────────────────────────────────────────────
let currentProgress: ProgressFinisher | null = null;

router.beforeEach(() => {
  // si hay overlay abierto, no queremos animar navegación
  if (hasOverlayOpen.value) return;
  // arrancar barra de progreso
  currentProgress = useProgress().start();

  // cerrar sidebar móvil en cada navegación
  appOption.appSidebarMobileToggled = false;

  // scroll al top
  document.body.scrollTop = 0;
  document.documentElement.scrollTop = 0;
});

router.afterEach(() => {
  // asegurar cierre de la barra de progreso
  try {
    // aquí podrías hacer lógica post-navegación si la necesitas
  } finally {
    currentProgress?.finish();
    currentProgress = null;
  }
});

// (opcional) por si hay errores de navegación, nos aseguramos de cerrar la barra
router.onError(() => {
  currentProgress?.finish();
  currentProgress = null;
});
</script>

<template>
  <div
    class="app"
    :class="{
      'app-header-menu-search-toggled': appOption.appHeaderSearchToggled,
      'app-header-fixed': appOption.appHeaderFixed,
      'app-sidebar-fixed': appOption.appSidebarFixed,
      'app-sidebar-grid': appOption.appSidebarGrid,
      'app-sidebar-toggled': appOption.appSidebarToggled,
      'app-sidebar-collapsed': appOption.appSidebarCollapsed,
      'app-sidebar-mobile-toggled': appOption.appSidebarMobileToggled,
      'app-sidebar-mobile-closed': appOption.appSidebarMobileClosed,
      'app-sidebar-end-toggled': appOption.appSidebarEndToggled,
      'app-sidebar-end-mobile-toggled': appOption.appSidebarEndMobileToggled,
      'app-content-full-height': appOption.appContentFullHeight,
      'app-content-full-width': appOption.appSidebarHide,
      'app-without-sidebar': appOption.appSidebarHide,
      'app-with-end-sidebar': appOption.appSidebarEnd,
      'app-with-wide-sidebar': appOption.appSidebarWide,
      'app-with-hover-sidebar': appOption.appSidebarHover,
      'app-with-top-menu': appOption.appTopMenu,
      'app-with-two-sidebar': appOption.appSidebarTwo,
      'pt-0': appOption.appHeaderHide,
      'app-boxed-layout': appOption.appBoxedLayout,
      'app-footer-fixed': appOption.appFooterFixed,
      'app-sidebar-minified': appOption.appSidebarMinified,
      'app-gradient-enabled': appOption.appGradientEnabled,
    }"
  >
    <vue3-progress-bar />

    <!-- Shell de layout -->
    <app-header v-if="!appOption.appHeaderHide" />
    <app-sidebar v-if="!appOption.appSidebarHide" />
    <app-top-menu v-if="appOption.appTopMenu" />

    <!-- Contenido principal -->
    <div class="app-content" :class="appOption.appContentClass">
      <router-view />

      <!-- Teleports globales -->
      <Teleport to="body">
        <RetryModal
          v-if="!orientationActive && showRetryModal"
          @retry="retry"
        />
        <!-- Chip/banner PWA: visible en el login -->
        <PWAUpdateBannerIonic
          v-if="!orientationActive"
          :fab-visible="isLoginRoute"
        />
        <!-- 🆕 Aviso global: solo se muestra en móviles y solo en LANDSCAPE -->
        <OrientationWarningMobile
          v-if="!hasSpiOrientationException"
          scope="auto"
          :messages="{
            phoneVertical: 'Por favor, usa el dispositivo en vertical',
            tabletDesktopHorizontal:
              'Por favor, usa el dispositivo en horizontal',
          }"
          @visible-change="orientationActive = $event"
        />
      </Teleport>
    </div>

    <app-footer v-if="appOption.appFooter" />
    <app-theme-panel />
  </div>
</template>

<style>
/* Banner de ejemplo (desactivado)
.update-banner {
  position: fixed;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  background: #ffc;
  padding: 0.5rem 1rem;
  border: 1px solid #cc9;
  border-radius: 0.25rem;
  display: flex;
  gap: 0.5rem;
  align-items: center;
  z-index: 10000;
}
*/
</style>
